import { forwardRef } from 'react'

import logoSvg from '@/assets/logo.svg'
import { cn } from '@/lib/utils'
import { FxIcon } from './FxIcon'

/**
 * The bar the rail becomes below 992px: the logo at the left, the page's own
 * actions and the hamburger at the right.
 *
 * The design system has no `TopBar` component — the README only says the rail
 * "becomes a hamburger in a top bar", and the phone artboards draw it. It is
 * a component here rather than markup in every screen so that no screen
 * reimplements navigation; `FxNavShell` renders it and owns the drawer the
 * hamburger opens.
 *
 * The tap targets are 44px, as the phone artboards draw them, which is larger
 * than the 40px `control` height the desktop uses.
 */
export interface FxTopBarProps extends React.HTMLAttributes<HTMLElement> {
  logoSrc?: string
  /**
   * Called by the hamburger. Omitted, the hamburger is not drawn. It is
   * handed the event so the caller can keep the button and give focus back
   * to it when the drawer closes.
   */
  onMenu?: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** Whether the drawer the hamburger opens is open, for `aria-expanded`. */
  menuOpen?: boolean
  /** The id of the drawer, for `aria-controls`. */
  menuControls?: string
  /** Page actions left of the hamburger — search, notifications, the account. */
  actions?: React.ReactNode
}

export const FxTopBar = forwardRef<HTMLElement, FxTopBarProps>(
  (
    { logoSrc = logoSvg, onMenu, menuOpen = false, menuControls, actions, className, ...props },
    ref,
  ) => (
    <header
      ref={ref}
      className={cn(
        'flex min-h-list-row items-center justify-between gap-4 bg-surface px-5 py-2',
        className,
      )}
      {...props}
    >
      <img src={logoSrc} alt="formalogix" width={40} height={40} className="shrink-0" />
      <div className="flex items-center gap-2">
        {actions}
        {onMenu && (
          <button
            type="button"
            aria-label="Menü"
            aria-expanded={menuOpen}
            aria-controls={menuControls}
            className="flex h-11 w-11 items-center justify-center rounded-sm text-primary-text"
            onClick={onMenu}
          >
            <FxIcon name="menu" size={32} />
          </button>
        )}
      </div>
    </header>
  ),
)
FxTopBar.displayName = 'FxTopBar'
